// ---------------------------------------------------------------------------
// Input — unified pointer/keyboard interaction, renderer-agnostic.
//
// Two control schemes (Settings):
//   classic  left-drag on empty table pans the camera (WASD also pans)
//   wasd     WASD is the only camera movement; left-drag on empty table
//            rubber-band selects multiple objects (screen-space projection)
//
// Core verbs (defaults; all rebindable in Settings):
//   drag object            move it (drag any selected object moves them all)
//   drag a deck/stack      pull the TOP card/chip off  (the TTS signature move)
//   shift+drag deck        move the whole deck
//   wheel while dragging   rotate held object
//   double-click deck      draw 1 to your hand · double-click card: flip
//   F flip · Q/E rotate · R shuffle · L lock · Del delete · 1-9 draw N
//   right-drag / middle    camera · wheel zoom · right-click menus
// ---------------------------------------------------------------------------

import { getDef } from "../shared/defs";
import { normAngle } from "../shared/math";
import { CURSOR_HZ, MOVE_HZ, type Intent } from "../shared/protocol";
import { findMergeTarget, findHandZone, hoverYAt, inZone, isContainer, type GameObject } from "../shared/state";
import { getTableSpec } from "../shared/tables";
import type { Net } from "./net";
import type { Replica } from "./replica";
import type { Renderer } from "./renderer";
import type { Settings } from "./settings";
import type { MenuItem, UI } from "./ui";

const DRAG_THRESHOLD = 5;
/** Hold a stack this long before dragging to grab the WHOLE stack. */
const STACK_HOLD_MS = 350;

interface HeldEntry {
  id: string;
  /** Offset from the drag anchor point on the table. */
  dx: number;
  dz: number;
}

interface DragState {
  /** Primary object (null while waiting to adopt a server-side pull). */
  id: string | null;
  /** Everything being dragged (primary first). Group drags have several. */
  held: HeldEntry[];
  pointerId: number;
  startX: number;
  startY: number;
  downAt: number;
  active: boolean;
  /** On a stack: whether this drag pulls the top item (quick drag) or moves
   *  the whole stack (shift, or held ≥ STACK_HOLD_MS before dragging). */
  isStack: boolean;
  shift: boolean;
  /** Any of ctrl/cmd/shift was held at pointer-down (multi-select click). */
  modifier: boolean;
  awaitingAdopt: boolean;
  yaw: number;
  /** Set when the drag originates from an inspector cell. */
  inspectorTake?: { id: string; index: number };
}

interface CamDrag {
  kind: "pan" | "orbit";
  pointerId: number;
  lastX: number;
  lastY: number;
  moved: number;
  button: number;
}

interface BoxSelect {
  pointerId: number;
  startX: number;
  startY: number;
  el: HTMLDivElement;
}

export class Input {
  hoveredId: string | null = null;
  private drag: DragState | null = null;
  private camDrag: CamDrag | null = null;
  private box: BoxSelect | null = null;
  private lastMoveSent = 0;
  private lastCursorSent = 0;
  private heldKeys = new Set<string>();
  private holdTimer: ReturnType<typeof setTimeout> | null = null;

  constructor(
    private container: HTMLElement,
    private getRenderer: () => Renderer,
    private state: Replica,
    private net: Net,
    private ui: UI,
    private settings: Settings,
  ) {
    const el = container;
    el.addEventListener("pointerdown", (e) => this.onDown(e));
    window.addEventListener("pointermove", (e) => this.onMove(e));
    window.addEventListener("pointerup", (e) => this.onUp(e));
    el.addEventListener("wheel", (e) => this.onWheel(e), { passive: false });
    el.addEventListener("dblclick", (e) => this.onDblClick(e));
    el.addEventListener("contextmenu", (e) => e.preventDefault());
    window.addEventListener("keydown", (e) => this.onKey(e), true);
    window.addEventListener("keyup", (e) => this.heldKeys.delete(e.key.toLowerCase()));
    window.addEventListener("blur", () => this.heldKeys.clear());
  }

  private send(i: Intent): void {
    this.net.send(i);
  }

  /** The deck sitting on your library slot, if the commander table is set. */
  private myLibraryDeck(): string | null {
    const slot = [...this.state.objs.values()].find(
      (o) => o.def === "zone.slot" && o.slot === "library" && o.seat === this.state.seat,
    );
    if (!slot) return null;
    for (const o of this.state.objs.values()) {
      if (getDef(o.def).kind === "stack" && Math.hypot(o.rp.x - slot.rp.x, o.rp.z - slot.rp.z) < 5) {
        return o.id;
      }
    }
    return null;
  }

  /** Rotation step (radians) from settings — default 90°. */
  private rotStep(): number {
    return ((this.settings.data.rotateStep || 90) * Math.PI) / 180;
  }

  private obj(id: string | null) {
    return id ? this.state.objs.get(id) : undefined;
  }

  /** Per-frame: keyboard camera panning + card-zoom hold. Called by main loop. */
  tick(dt: number): void {
    this.updateZoom();
    if (this.heldKeys.size === 0) return;
    const b = this.settings.data.binds;
    let dirX = 0, dirY = 0;
    if (this.heldKeys.has(b.panUp)) dirY += 1;
    if (this.heldKeys.has(b.panDown)) dirY -= 1;
    if (this.heldKeys.has(b.panLeft)) dirX -= 1;
    if (this.heldKeys.has(b.panRight)) dirX += 1;
    if (dirX || dirY) {
      this.getRenderer().panKeys(dirX, dirY, dt * this.settings.data.panSpeed);
    }
  }

  /** Holding the zoom key over a readable card shows the magnified view. */
  private updateZoom(): void {
    const bind = this.settings.data.binds.zoomCard;
    let def: string | null = null;
    if (bind && this.heldKeys.has(bind)) {
      // Inspector cells take priority over table hover.
      def = this.ui.inspectorHoveredDef;
      const o = def ? undefined : this.obj(this.hoveredId);
      if (o) {
        const d = getDef(o.def);
        if (d.kind === "card" && o.faceUp && d.front) def = o.def;
        else if (d.kind === "stack" && o.faceUp && o.topDef) def = o.topDef;
        else if ((d.kind === "card" || d.kind === "stack") && d.back) def = null;
      }
    }
    this.ui.setZoomCard(def);
  }

  /** Start dragging a card out of the inspector panel (wired from main). */
  startInspectorDrag(containerId: string, index: number, e: PointerEvent): void {
    e.preventDefault();
    this.drag = {
      id: null,
      held: [],
      pointerId: e.pointerId,
      startX: e.clientX,
      startY: e.clientY,
      downAt: performance.now(),
      active: false,
      isStack: false,
      shift: false,
      modifier: false,
      awaitingAdopt: false,
      yaw: 0,
      inspectorTake: { id: containerId, index },
    };
  }

  /** Wired from main: server confirmed something is now held by us. */
  adoptHold(id: string): void {
    if (this.drag?.awaitingAdopt) {
      this.drag.id = id;
      this.drag.held = [{ id, dx: 0, dz: 0 }];
      this.drag.awaitingAdopt = false;
      this.state.localHolds.add(id);
      const o = this.obj(id);
      if (o) this.drag.yaw = o.yaw;
    }
  }

  /** Server refused an action — abandon any in-flight drag. */
  cancelDrag(): void {
    if (this.drag) {
      this.state.localHolds.clear();
      this.drag = null;
    }
  }

  // ------------------------------------------------------------- pointer

  private onDown(e: PointerEvent): void {
    this.ui.hideMenu();

    if (e.button === 0) {
      const r = this.getRenderer();
      const id = r.pick(e.clientX, e.clientY, this.state);
      const o = this.obj(id);
      if (id && o && !o.locked) {
        const d = getDef(o.def);
        const isStack = d.kind === "stack";
        const modifier = e.ctrlKey || e.metaKey || e.shiftKey;
        const inSelection = this.state.selected.has(id) && this.state.selected.size > 1;
        // Modifier-click keeps the current selection; the click (on pointer
        // UP, if no drag happened) toggles membership instead of replacing.
        if (!inSelection && !modifier) this.state.selected.clear();
        this.drag = {
          id,
          held: [],
          pointerId: e.pointerId,
          startX: e.clientX,
          startY: e.clientY,
          downAt: performance.now(),
          active: false,
          isStack: isStack && !inSelection,
          shift: e.shiftKey || e.ctrlKey || e.metaKey,
          modifier,
          awaitingAdopt: false,
          yaw: o.yaw,
        };
        if (this.drag.isStack && !modifier) {
          // The whole-stack grab announces itself: lift as soon as the hold
          // timer expires, before any pointer movement.
          this.holdTimer = setTimeout(() => {
            this.holdTimer = null;
            if (this.drag && this.drag.id === id && !this.drag.active) {
              this.drag.active = true;
              this.liftWhole(this.drag);
            }
          }, STACK_HOLD_MS);
        }
      } else {
        if (!(e.ctrlKey || e.metaKey || e.shiftKey)) this.state.selected.clear();
        if (this.settings.data.scheme === "wasd") {
          // Rubber-band select from the pointer's projected drag.
          const el = document.createElement("div");
          el.className = "select-box";
          document.body.append(el);
          this.box = { pointerId: e.pointerId, startX: e.clientX, startY: e.clientY, el };
          this.updateBox(e.clientX, e.clientY);
        } else {
          this.camDrag = { kind: "pan", pointerId: e.pointerId, lastX: e.clientX, lastY: e.clientY, moved: 0, button: 0 };
        }
      }
    } else if (e.button === 1) {
      e.preventDefault();
      this.camDrag = { kind: "pan", pointerId: e.pointerId, lastX: e.clientX, lastY: e.clientY, moved: 0, button: 1 };
    } else if (e.button === 2) {
      this.camDrag = { kind: "orbit", pointerId: e.pointerId, lastX: e.clientX, lastY: e.clientY, moved: 0, button: 2 };
    }
  }

  private onMove(e: PointerEvent): void {
    const r = this.getRenderer();

    if (this.box && e.pointerId === this.box.pointerId) {
      this.updateBox(e.clientX, e.clientY);
      return;
    }

    if (this.camDrag && e.pointerId === this.camDrag.pointerId) {
      const dx = e.clientX - this.camDrag.lastX;
      const dy = e.clientY - this.camDrag.lastY;
      this.camDrag.moved += Math.abs(dx) + Math.abs(dy);
      this.camDrag.lastX = e.clientX;
      this.camDrag.lastY = e.clientY;
      if (this.camDrag.moved > 3) r.cameraDrag(this.camDrag.kind, dx, dy);
      return;
    }

    if (this.drag && e.pointerId === this.drag.pointerId) {
      const dist = Math.hypot(e.clientX - this.drag.startX, e.clientY - this.drag.startY);
      if (!this.drag.active && dist > DRAG_THRESHOLD) {
        this.drag.active = true;
        this.beginDrag(e);
      }
      if (this.drag.active && this.drag.held.length > 0) {
        const pt = r.screenToTable(e.clientX, e.clientY);
        if (pt) this.dragTo(pt.x, pt.z);
      }
      return;
    }

    // Plain hover.
    this.hoveredId = r.pick(e.clientX, e.clientY, this.state);
    const now = performance.now();
    if (now - this.lastCursorSent > 1000 / CURSOR_HZ) {
      this.lastCursorSent = now;
      const pt = r.screenToTable(e.clientX, e.clientY);
      if (pt) this.send({ t: "cursor", x: pt.x, z: pt.z });
    }
  }

  /** Threshold crossed: actually pick things up. */
  private beginDrag(e: PointerEvent): void {
    const drag = this.drag!;
    const r = this.getRenderer();
    const anchor = r.screenToTable(e.clientX, e.clientY);

    if (drag.inspectorTake) {
      // Dragging a card straight out of the inspector panel.
      drag.awaitingAdopt = true;
      this.send({ t: "takeAt", id: drag.inspectorTake.id, index: drag.inspectorTake.index, dest: "drag" });
      return;
    }

    // Quick drag on a stack pulls the top item; holding the press for a
    // moment first (or shift) grabs the whole stack (the hold timer may have
    // already lifted it — then held[] is populated and we're done here).
    if (drag.held.length > 0) return;
    const heldLong = performance.now() - drag.downAt >= STACK_HOLD_MS;
    if (drag.isStack && !drag.shift && !heldLong) {
      // Ask the server to pull the top item; we adopt the new object
      // when its upsert (heldBy = us) arrives.
      drag.awaitingAdopt = true;
      this.send({ t: "pickup", id: drag.id!, split: true });
      drag.id = null;
      return;
    }

    this.liftWhole(drag, anchor);
  }

  /** Pick up the drag's object(s) whole (never a top-card pull). */
  private liftWhole(drag: DragState, anchor?: { x: number; z: number } | null): void {
    const ids =
      this.state.selected.has(drag.id!) && this.state.selected.size > 1
        ? [drag.id!, ...[...this.state.selected].filter((x) => x !== drag.id)]
        : [drag.id!];
    const ref = anchor ?? (() => {
      const o = this.obj(drag.id!);
      return o ? { x: o.pos.x, z: o.pos.z } : null;
    })();
    drag.held = ids.map((id) => {
      const o = this.obj(id)!;
      return ref
        ? { id, dx: o.pos.x - ref.x, dz: o.pos.z - ref.z }
        : { id, dx: 0, dz: 0 };
    });
    for (const h of drag.held) {
      this.state.localHolds.add(h.id);
      this.send({ t: "pickup", id: h.id });
    }
  }

  private dragTo(x: number, z: number): void {
    const drag = this.drag!;
    for (const h of drag.held) {
      const o = this.obj(h.id);
      if (!o) continue;
      o.pos.x = x + h.dx;
      o.pos.z = z + h.dz;
      // Predict the server's hover rule: ride above whatever is underneath.
      // Exclude everything we're locally holding — the server's heldBy flags
      // haven't round-tripped yet, and a held object counting as its own
      // support climbs by its own height every pointer move.
      o.pos.y = hoverYAt(this.state.objs.values(), o, o.pos.x, o.pos.z, this.state.localHolds);
      if (h.id === drag.id) o.yaw = drag.yaw;
    }
    this.updateDropPreview(x, z);
    const now = performance.now();
    if (now - this.lastMoveSent > 1000 / MOVE_HZ) {
      this.lastMoveSent = now;
      if (drag.held.length === 1) {
        this.send({ t: "move", id: drag.held[0]!.id, x: x + drag.held[0]!.dx, z: z + drag.held[0]!.dz, yaw: drag.yaw });
      } else {
        this.send({
          t: "moveMany",
          o: drag.held.map((h) => {
            const o = this.obj(h.id)!;
            return [h.id, x + h.dx, z + h.dz, o.yaw] as [string, number, number, number];
          }),
        });
      }
    }
  }

  /** Predict where the primary held object will land (target vs ghost). */
  private updateDropPreview(x: number, z: number): void {
    const drag = this.drag;
    const primary = drag?.held[0] ? this.obj(drag.held[0].id) : undefined;
    if (!drag || !primary) {
      this.state.dropTargetId = null;
      this.state.dropGhost = null;
      return;
    }
    const px = x + drag.held[0]!.dx, pz = z + drag.held[0]!.dz;
    const objs = this.state.objs.values() as Iterable<GameObject>;
    const zone = findHandZone(objs, px, pz);
    const target = zone ? null : findMergeTarget(this.state.objs.values() as Iterable<GameObject>, primary as unknown as GameObject, px, pz);
    if (target) {
      this.state.dropTargetId = target.id;
      this.state.dropGhost = null;
    } else {
      const d = getDef(primary.def);
      this.state.dropTargetId = null;
      this.state.dropGhost = {
        x: px, z: pz, yaw: primary.yaw,
        w: d.size[0], d: d.size[2],
        cyl: d.shape === "cylinder",
      };
    }
  }

  private onUp(e: PointerEvent): void {
    const r = this.getRenderer();

    if (this.box && e.pointerId === this.box.pointerId) {
      this.finishBox(e.clientX, e.clientY);
      return;
    }

    if (this.camDrag && e.pointerId === this.camDrag.pointerId) {
      const cd = this.camDrag;
      this.camDrag = null;
      if (cd.button === 2 && cd.moved <= 3) this.openMenu(e.clientX, e.clientY);
      return;
    }

    if (this.drag && e.pointerId === this.drag.pointerId) {
      if (this.holdTimer) {
        clearTimeout(this.holdTimer);
        this.holdTimer = null;
      }
      const drag = this.drag;
      this.drag = null;
      this.state.dropTargetId = null;
      this.state.dropGhost = null;
      if (!drag.active) {
        if (drag.inspectorTake) {
          // Plain click on an inspector card: take it to hand.
          this.send({ t: "takeAt", id: drag.inspectorTake.id, index: drag.inspectorTake.index, dest: "hand" });
        } else if (drag.id) {
          if (drag.modifier) {
            // Modifier-click: toggle this object in the selection.
            if (this.state.selected.has(drag.id)) this.state.selected.delete(drag.id);
            else this.state.selected.add(drag.id);
          } else {
            // Plain click: select just this object.
            this.state.selected.clear();
            this.state.selected.add(drag.id);
          }
        }
        return;
      }
      // Dropping onto the open deck view inserts into that container.
      if (drag.held.length > 0 && this.ui.isOverInspector(e.clientX, e.clientY) && this.ui.openInspectorId) {
        const container = this.ui.openInspectorId;
        for (const h of drag.held) {
          this.send({ t: "putInto", id: h.id, container });
          this.state.localHolds.delete(h.id);
        }
        return;
      }
      const pt = r.screenToTable(e.clientX, e.clientY);
      if (drag.held.length === 1 && pt) {
        const h = drag.held[0]!;
        this.send({ t: "release", id: h.id, x: pt.x + h.dx, z: pt.z + h.dz });
        this.state.localHolds.delete(h.id);
      } else if (drag.held.length > 1 && pt) {
        // Group drop: if the card under the cursor lands on a deck, the
        // server stacks the WHOLE selection into it.
        const primary = drag.held[0]!;
        this.send({
          t: "releaseMany",
          o: drag.held.map((h) => [h.id, pt.x + h.dx, pt.z + h.dz] as [string, number, number]),
          tx: pt.x + primary.dx,
          tz: pt.z + primary.dz,
        });
        for (const h of drag.held) this.state.localHolds.delete(h.id);
      } else if (drag.awaitingAdopt) {
        // Pickup confirmation hasn't arrived yet; release wherever it lands.
        setTimeout(() => {
          const held = [...this.state.localHolds][0];
          if (held && pt) {
            this.send({ t: "release", id: held, x: pt.x, z: pt.z });
          }
          this.state.localHolds.clear();
        }, 120);
        return;
      }
      this.state.localHolds.clear();
    }
  }

  // ----------------------------------------------------------- box select

  private updateBox(x: number, y: number): void {
    const b = this.box!;
    const left = Math.min(b.startX, x), top = Math.min(b.startY, y);
    b.el.style.left = `${left}px`;
    b.el.style.top = `${top}px`;
    b.el.style.width = `${Math.abs(x - b.startX)}px`;
    b.el.style.height = `${Math.abs(y - b.startY)}px`;
  }

  private finishBox(x: number, y: number): void {
    const b = this.box!;
    this.box = null;
    b.el.remove();
    const r = this.getRenderer();
    const x0 = Math.min(b.startX, x), x1 = Math.max(b.startX, x);
    const y0 = Math.min(b.startY, y), y1 = Math.max(b.startY, y);
    if (x1 - x0 < 6 && y1 - y0 < 6) return; // just a click
    this.state.selected.clear();
    for (const o of this.state.objs.values()) {
      const d = getDef(o.def);
      if (d.kind === "zone" || o.locked || o.heldBy) continue;
      const p = r.projectToScreen(o.rp.x, o.rp.z, o.rp.y);
      if (p && p.x >= x0 && p.x <= x1 && p.y >= y0 && p.y <= y1) {
        this.state.selected.add(o.id);
      }
    }
  }

  // ------------------------------------------------------------ mouse etc

  private onWheel(e: WheelEvent): void {
    e.preventDefault();
    const r = this.getRenderer();
    if (this.ui.zoomVisible) {
      // Wheel resizes the magnified card while it's up.
      this.ui.scaleZoom(e.deltaY);
      return;
    }
    if (this.drag?.active) {
      // Rotate the held object (the primary one).
      const step = this.rotStep() * Math.sign(e.deltaY);
      this.drag.yaw = normAngle(this.drag.yaw + step);
      const o = this.obj(this.drag.id);
      if (o && this.drag.id) {
        o.yaw = this.drag.yaw;
        this.send({ t: "move", id: this.drag.id, x: o.pos.x, z: o.pos.z, yaw: this.drag.yaw });
      }
      return;
    }
    r.cameraZoom(e.deltaY, e.clientX, e.clientY);
  }

  private onDblClick(e: MouseEvent): void {
    const r = this.getRenderer();
    const id = r.pick(e.clientX, e.clientY, this.state);
    const o = this.obj(id);
    if (!o || !id) return;
    const d = getDef(o.def);
    if (d.kind === "stack") this.send({ t: "draw", id, n: 1 });
    else if (d.kind === "die") this.send({ t: "roll", ids: this.rollTargets(id) });
    else this.send({ t: "flip", id });
  }

  /** Rolling a selected die rolls every selected die with it. */
  private rollTargets(id: string): string[] {
    const ids = this.state.selected.has(id) && this.state.selected.size > 1 ? [...this.state.selected] : [id];
    return ids.filter((x) => getDef(this.obj(x)?.def ?? "").kind === "die");
  }

  // ------------------------------------------------------------ keyboard

  private onKey(e: KeyboardEvent): void {
    // Settings key-capture (rebinding) eats everything first.
    if (this.settings.handleKeyCapture(e)) return;
    if (e.target instanceof HTMLInputElement || e.target instanceof HTMLTextAreaElement || e.target instanceof HTMLSelectElement) return;

    const key = e.key.toLowerCase();
    this.heldKeys.add(key);
    if ((e.ctrlKey || e.metaKey) && key === "z") {
      e.preventDefault();
      this.send({ t: e.shiftKey ? "redo" : "undo" });
      return;
    }
    if (key === "escape") {
      this.ui.escapeCloseAll();
      this.settings.close();
      this.state.selected.clear();
      return;
    }
    // The zoom key is a hold (often Alt) — stop browser menu focus etc.
    if (key === this.settings.data.binds.zoomCard) e.preventDefault();
    let action = this.settings.actionFor(key);
    // Mac keyboards: the "delete" key reports as Backspace.
    if (!action && key === "backspace") action = "delete";
    if (action) e.preventDefault();

    // Targets: the whole selection if one exists, else drag/hover target.
    const primary = this.drag?.id ?? this.hoveredId;
    const targets =
      this.state.selected.size > 0 && (!primary || this.state.selected.has(primary))
        ? [...this.state.selected]
        : primary ? [primary] : [];

    switch (action) {
      case "flip":
        for (const id of targets) this.send({ t: "flip", id });
        return;
      case "rotateLeft":
      case "rotateRight": {
        const step = this.rotStep() * (action === "rotateLeft" ? -1 : 1);
        for (const id of targets) {
          const o = this.obj(id);
          if (!o) continue;
          if (this.drag?.id === id) {
            this.drag.yaw = normAngle(this.drag.yaw + step);
            o.yaw = this.drag.yaw;
            this.send({ t: "move", id, x: o.pos.x, z: o.pos.z, yaw: this.drag.yaw });
          } else {
            this.send({ t: "rotate", id, yaw: normAngle(o.yaw + step) });
          }
        }
        return;
      }
      case "shuffle": {
        // The shuffle bind doubles as "roll" on dice.
        const dice = targets.filter((id) => getDef(this.obj(id)?.def ?? "").kind === "die");
        if (dice.length) this.send({ t: "roll", ids: dice });
        for (const id of targets) {
          if (!dice.includes(id)) this.send({ t: "shuffle", id });
        }
        return;
      }
      case "lock":
        for (const id of targets) this.send({ t: "lock", id });
        return;
      case "delete":
        for (const id of targets) this.send({ t: "delete", id });
        this.state.selected.clear();
        return;
      case "toggleMode":
        this.ui.onModeToggle();
        return;
      case "help":
        this.ui.toggleHelp();
        return;
      case "sortHand":
        this.send({ t: "sortHand" });
        return;
      case "findCard":
        this.openFindBar();
        return;
      case "chat":
        this.ui.openChat();
        return;
      case "drawCard": {
        // Draw from the hovered deck, else your library pile.
        const o2 = this.obj(this.hoveredId);
        if (o2 && this.hoveredId && getDef(o2.def).kind === "stack") {
          this.send({ t: "draw", id: this.hoveredId, n: 1 });
        } else {
          const lib = this.myLibraryDeck();
          if (lib) this.send({ t: "draw", id: lib, n: 1 });
        }
        return;
      }
      case "toGrave":
        if (targets.length) this.send({ t: "toSlot", ids: targets, slot: "graveyard" });
        return;
      case "toExile":
        if (targets.length) this.send({ t: "toSlot", ids: targets, slot: "exile" });
        return;
      case "toHand":
        if (targets.length) this.send({ t: "toHand", ids: targets });
        return;
      case "mulligan":
        this.ui.confirm("Shuffle your hand into your deck and draw 7?", () => this.send({ t: "mulligan", n: 7 }));
        return;
      case "newTurn":
        this.send({ t: "newTurn" });
        return;
      case "plusOne":
        for (const id of targets) this.send({ t: "counter", id, cid: "p1p1", label: "+1/+1", delta: 1 });
        return;
      case "lifeUp":
        this.send({ t: "life", seat: this.state.seat, delta: 1 });
        return;
      case "lifeDown":
        this.send({ t: "life", seat: this.state.seat, delta: -1 });
        return;
    }

    // Draw N from a hovered container.
    if (/^[1-9]$/.test(key)) {
      const o = this.obj(this.hoveredId);
      if (o && this.hoveredId && getDef(o.def).kind === "stack") {
        this.send({ t: "draw", id: this.hoveredId, n: Number(key) });
      }
    }
  }

  // ----------------------------------------------------------- find card

  /** The sender's own hand zone object, if the table has one. */
  private myHandZone() {
    return [...this.state.objs.values()].find(
      (o) => o.def === "zone.hand" && o.seat === this.state.seat,
    );
  }

  /** Search-select cards in your hand: typing highlights matches. */
  private openFindBar(): void {
    const zone = this.myHandZone();
    if (!zone) {
      this.ui.toast("You don't have a hand zone on this table.");
      return;
    }
    this.ui.showFindBar(
      (q) => {
        this.state.selected.clear();
        if (!q) return 0;
        const terms = q.toLowerCase().split(/\s+/);
        let n = 0;
        for (const o of this.state.objs.values()) {
          if (o.inZone !== zone.id) continue;
          const d = getDef(o.def);
          const name = (o.name ?? d.name).toLowerCase();
          const type = (d.meta?.type ?? "").toLowerCase();
          // Same grammar as the deck-view search: all terms must match,
          // t:/type: prefixed terms match the type line.
          const ok = terms.every((term) => {
            const m = /^(t|type):(.+)$/.exec(term);
            if (m) return type.includes(m[2]!);
            return name.includes(term);
          });
          if (ok) {
            this.state.selected.add(o.id);
            n++;
          }
        }
        return n;
      },
      (keep) => {
        if (!keep) this.state.selected.clear();
      },
    );
  }

  // -------------------------------------------------------- context menu

  private openMenu(x: number, y: number): void {
    const r = this.getRenderer();
    const id = r.pick(x, y, this.state);
    const o = this.obj(id);
    const pt = r.screenToTable(x, y);
    const k = (a: Parameters<Settings["keyFor"]>[0]) =>
      this.settings.keyFor(a) ? `  (${this.settings.keyFor(a).length === 1 ? this.settings.keyFor(a).toUpperCase() : this.settings.keyFor(a)})` : "";

    if (o && id) {
      const d = getDef(o.def);
      const items: MenuItem[] = [];
      // Which slot kinds this table actually has (menus adapt per table type).
      const hasSlot = (kind: string) =>
        [...this.state.objs.values()].some((z) => z.def === "zone.slot" && z.slot === kind);
      if (d.kind === "stack") {
        const n = o.count ?? 0;
        items.push(
          ["Draw 1 to hand", () => this.send({ t: "draw", id, n: 1 })],
          ["Draw opening hand (7)", () => this.send({ t: "draw", id, n: 7 })],
          [`Shuffle${k("shuffle")}`, () => this.send({ t: "shuffle", id })],
          ["Search contents…", () => this.send({ t: "inspect", id })],
        );
        if (d.acceptsGroup?.startsWith("cards")) {
          // Table types with a canonical hand size (Hearts: 13) get it as a
          // one-click entry heading the deal submenu.
          const dn = getTableSpec(this.state.table.type).deal;
          items.push({
            label: "Deal to players",
            children: [
              ...(dn ? [[`Deal ${dn} to each player`, () => this.send({ t: "deal", id, n: dn })] as [string, () => void]] : []),
              ["Deal 1 to each player", () => this.send({ t: "deal", id, n: 1 })],
              ["Deal 5 to each player", () => this.send({ t: "deal", id, n: 5 })],
              ["Deal N to each…", () => {
                const nn = Number(prompt("Deal how many cards to each player?", "5"));
                if (nn > 0) this.send({ t: "deal", id, n: nn });
              }],
            ],
          });
          if (hasSlot("prize")) items.push(["Set out 6 prizes", () => this.send({ t: "prizes", id })]);
        }
        const ask = (q: string, fn: (n: number) => void) => () => {
          const nn = Number(prompt(q, "1"));
          if (nn > 0) fn(nn);
        };
        items.push({
          label: "Top of deck",
          children: [
            ["Reveal top…", ask("Reveal how many?", (nn) => this.send({ t: "reveal", id, n: nn }))],
            ["Scry…", ask("Scry how many?", (nn) => this.send({ t: "scry", id, n: nn, mode: "scry" }))],
            ["Surveil…", ask("Surveil how many?", (nn) => this.send({ t: "scry", id, n: nn, mode: "surveil" }))],
            ["Mill…", ask("Mill how many?", (nn) => this.send({ t: "mill", id, n: nn }))],
          ],
        });
        if (n > 1) items.push([`Split in half (${Math.floor(n / 2)})`, () => this.send({ t: "split", id, n: Math.floor(n / 2) })]);
        items.push([`Flip${k("flip")}`, () => this.send({ t: "flip", id })]);
      } else if (d.kind === "die") {
        const dice = this.rollTargets(id);
        items.push(
          [d.cycle && dice.length === 1 ? `Turn the cube (double)${k("shuffle")}`
            : dice.length > 1 ? `Roll ${dice.length} dice${k("shuffle")}` : `Roll${k("shuffle")}`,
            () => this.send({ t: "roll", ids: dice })],
        );
      } else {
        items.push([`Flip${k("flip")}`, () => this.send({ t: "flip", id })]);
      }
      // The trick mat, or a card played onto it: collect the trick.
      const trickSlots = [...this.state.objs.values()].filter((z) => z.def === "zone.slot" && z.slot === "trick");
      if (trickSlots.length > 0 && (
        o.def === "board.trickmat" ||
        ((d.kind === "card" || d.kind === "stack") && !o.inZone && trickSlots.some((z) => inZone(z, o.pos.x, o.pos.z)))
      )) {
        items.push(["Take the trick", () => this.send({ t: "takeTrick" })]);
      }
      // A card sitting in YOUR hand: quick hand housekeeping.
      if (o.inZone && this.obj(o.inZone)?.seat === this.state.seat) {
        items.push(
          [`Sort hand${k("sortHand")}`, () => this.send({ t: "sortHand" })],
          [`Find in hand…${k("findCard")}`, () => this.openFindBar()],
        );
      }
      if (d.kind === "card" || d.kind === "stack") {
        const cTargets = this.state.selected.has(id) && this.state.selected.size > 1 ? [...this.state.selected] : [id];
        // Send-to destinations — only the ones this table actually has.
        const dests: MenuItem[] = [];
        const slotDests: Array<[string, "graveyard" | "exile" | "library" | "inkwell"]> = [
          [`To graveyard${k("toGrave")}`, "graveyard"],
          [`To exile${k("toExile")}`, "exile"],
          ["To top of library", "library"],
          ["To inkwell (face-down ink)", "inkwell"],
        ];
        for (const [label, slot] of slotDests) {
          if (hasSlot(slot)) dests.push([label, () => this.send({ t: "toSlot", ids: cTargets, slot })]);
        }
        if (this.myHandZone() && d.kind === "card") {
          dests.push([`To your hand${k("toHand")}`, () => this.send({ t: "toHand", ids: cTargets })]);
        }
        if (dests.length > 0) {
          items.push(dests.length === 1 ? dests[0]! : { label: cTargets.length > 1 ? `Send ${cTargets.length} to` : "Send to", children: dests });
        }
        items.push({
          label: "Counters",
          children: [
            ["＋ +1/+1 counter", () => cTargets.forEach((t2) => this.send({ t: "counter", id: t2, cid: "p1p1", label: "+1/+1", delta: 1 }))],
            ["＋ -1/-1 counter", () => cTargets.forEach((t2) => this.send({ t: "counter", id: t2, cid: "m1m1", label: "-1/-1", delta: 1 }))],
            ["＋ Counter…", () => {
              const label = prompt("Counter name (e.g. Loyalty, Charge):", "Charge");
              if (label) cTargets.forEach((t2) => this.send({ t: "counter", id: t2, cid: `custom:${label}`, label, delta: 1 }))
            }],
            ["− Remove a counter", () => {
              const o3 = this.obj(id);
              const c0 = o3?.counters?.[o3.counters.length - 1];
              if (c0) cTargets.forEach((t2) => this.send({ t: "counter", id: t2, cid: c0.id, label: c0.label, delta: -1 }));
            }],
          ],
        });
      }
      const bagIds = this.state.selected.has(id) && this.state.selected.size > 1
        ? [...this.state.selected]
        : [id];
      items.push(
        [bagIds.length > 1 ? `Save ${bagIds.length} to bag…` : "Save to bag…", () => {
          const name = prompt("Name for this bag entry:", getDef(o.def).name);
          if (name !== null) this.send({ t: "saveObjects", ids: bagIds, name: name || getDef(o.def).name });
        }],
        [o.locked ? `Unlock${k("lock")}` : `Lock${k("lock")}`, () => this.send({ t: "lock", id })],
        [`Delete${k("delete")}`, () => this.send({ t: "delete", id })],
      );
      this.ui.showMenu(items, x, y, getDef(o.def).name + (o.count ? ` ×${o.count}` : ""));
    } else if (pt) {
      this.ui.showMenu(
        [
          {
            label: "Spawn",
            children: [
              ["Shuffled deck (52)", () => this.send({ t: "spawnPreset", preset: "deck52", x: pt.x, z: pt.z })],
              ["Poker chips", () => this.send({ t: "spawnPreset", preset: "chips", x: pt.x, z: pt.z })],
              ["d6", () => this.send({ t: "spawn", def: "die.d6", x: pt.x, z: pt.z })],
              ["d20", () => this.send({ t: "spawn", def: "die.d20", x: pt.x, z: pt.z })],
              ["Dice set (d4–d20)", () => this.send({ t: "spawnPreset", preset: "dice", x: pt.x, z: pt.z })],
              ["Coin", () => this.send({ t: "spawn", def: "die.coin", x: pt.x, z: pt.z })],
              ["Dominoes (28)", () => this.send({ t: "spawnPreset", preset: "dominoes", x: pt.x, z: pt.z })],
              ["Doubling cube", () => this.send({ t: "spawn", def: "die.doubling", x: pt.x, z: pt.z })],
              ["Checkerboard", () => this.send({ t: "spawn", def: "board.checker", x: pt.x, z: pt.z })],
              ["Grid mat", () => this.send({ t: "spawn", def: "board.grid", x: pt.x, z: pt.z })],
              ["Pawn", () => this.send({ t: "spawn", def: `pawn.${Math.floor(Math.random() * 6)}`, x: pt.x, z: pt.z })],
              ["Counter chip…", () => this.ui.showCounterChipMenu(x, y, (color, label) => {
                this.send({ t: "spawnCounter", color, label, x: pt.x, z: pt.z });
              })],
            ],
          },
          ["Import deck (MTG / Pokémon / Lorcana)…", () => {
            window.dispatchEvent(new CustomEvent("tt-import-deck", {
              detail: { x: pt.x, z: pt.z, send: (i: Intent) => this.send(i) },
            }));
          }],
          ["Open bag…", () => this.ui.showBagPanel(
            this.settings,
            (data) => this.send({ t: "spawnSaved", data, x: pt.x, z: pt.z }),
          )],
          ["Undo (⌘Z)", () => this.send({ t: "undo" })],
          [`Reset this ${getTableSpec(this.state.table.type).name} table…`,
            () => this.ui.confirm("Reset the whole table?", () => this.send({ t: "resetTable" }))],
          ["Switch game — table picker…", () => this.ui.confirm(
            "Leave this table for the game picker? (The room keeps living at its link.)",
            () => { location.href = "/"; },
          )],
        ],
        x, y, "Table",
      );
    }
  }
}
